import {
  useNotifications,
  NotificationActionsCategory,
  UseNotificationsReturn,
} from '@organice/utils/hooks/useNotifications'
import { List, Spin, Button, Skeleton } from 'antd'
import React from 'react'
import styled from '@emotion/styled'
import { Notification } from '@organice/atoms'
import { Tabs } from 'antd'
import { useTranslation } from 'react-i18next'
import Icon from '@ant-design/icons'
import MailOpen from '@organice/icons/mail_open.svg?react'
import { useResponsive } from '@organice/utils/hooks/useResponsive'

enum TabKeys {
  Notifications = '1',
  Comments = '2',
}

const UnreadCount: React.FC<{ count: number | undefined }> = ({ count }) => {
  if (!count) {
    return null
  }
  return <>({count})</>
}

type NotificationsProps = {
  visible?: boolean
  notifications: UseNotificationsReturn
}

export const Notifications: React.FC<NotificationsProps> = ({
  visible,
  notifications,
}) => {
  const { t } = useTranslation()
  const {
    allNotifications,
    allCommentNotifications,
    unReadCommentNotificationsCount,
    unReadNotificationsCount,
    markAllNotificationsAsRead,
    setNotificationsReadAt,
    loading,
    settingReadAtLoading,
    fetchMoreNotifications,
    hasMoreNotifications,
    fetchMoreCommentNotifications,
    hasMoreCommentNotifications,
  } = notifications

  const { isMobileOrTablet } = useResponsive()

  const readNotification = (notificationIds: bigint[], read: boolean) => {
    setNotificationsReadAt(notificationIds, read)
  }

  const [activeKey, setActiveKey] = React.useState<string | undefined>('1')

  /* Notifications Autoload on Scroll */
  const needMoreNotificationsTriggerRef = React.useRef<HTMLDivElement>(null)
  const [needMoreNotifications, setNeedMoreNotifications] =
    React.useState(false)

  const notificationsObserver = React.useMemo(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 0,
    }

    return new IntersectionObserver(([entry]) => {
      if (Boolean(entry.isIntersecting) !== needMoreNotifications) {
        setNeedMoreNotifications(Boolean(entry.isIntersecting))
      }
    }, options)
  }, [needMoreNotifications])

  React.useEffect(() => {
    if (needMoreNotificationsTriggerRef.current) {
      notificationsObserver.observe(needMoreNotificationsTriggerRef.current)
    }

    return () => {
      notificationsObserver.disconnect()
    }
  }, [needMoreNotificationsTriggerRef, notificationsObserver])

  React.useEffect(() => {
    if (needMoreNotifications === true && hasMoreNotifications) {
      fetchMoreNotifications()
    }
  }, [needMoreNotifications])

  /* Comment Notifications Autoload on Scroll */
  const needMoreCommentNotificationsTriggerRef =
    React.useRef<HTMLDivElement>(null)
  const [needMoreCommentNotifications, setNeedMoreCommentNotifications] =
    React.useState(false)

  const commentsObserver = React.useMemo(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 0,
    }

    return new IntersectionObserver(([entry]) => {
      if (Boolean(entry.isIntersecting) !== needMoreCommentNotifications) {
        setNeedMoreCommentNotifications(Boolean(entry.isIntersecting))
      }
    }, options)
  }, [needMoreCommentNotifications])

  React.useEffect(() => {
    if (needMoreCommentNotificationsTriggerRef.current) {
      commentsObserver.observe(needMoreCommentNotificationsTriggerRef.current)
    }

    return () => {
      commentsObserver.disconnect()
    }
  }, [needMoreCommentNotificationsTriggerRef.current, commentsObserver])

  React.useEffect(() => {
    if (needMoreCommentNotifications === true && hasMoreCommentNotifications) {
      fetchMoreCommentNotifications()
    }
  }, [needMoreCommentNotifications])

  return (
    <NotificationsListContainer isMobileOrTablet={isMobileOrTablet}>
      <Tabs
        activeKey={activeKey}
        onChange={activeKey => setActiveKey(activeKey)}
      >
        <Tabs.TabPane
          key={TabKeys.Notifications}
          tab={
            <>
              {t('notifications.title')}{' '}
              {loading ? (
                <Skeleton
                  active
                  title={{ width: 20, style: { marginBottom: 0, height: 16 } }}
                  style={{
                    height: 12,
                    display: 'inline-flex',
                    marginLeft: 4,
                  }}
                  paragraph={false}
                />
              ) : (
                <UnreadCount count={unReadNotificationsCount} />
              )}
            </>
          }
        >
          {!allNotifications && loading ? (
            <NotificationsLoading />
          ) : (
            <>
              <List
                itemLayout="horizontal"
                dataSource={allNotifications}
                renderItem={(notification, i) => {
                  return (
                    <Notification
                      {...notification}
                      setReadHandler={readNotification}
                      loading={settingReadAtLoading}
                    />
                  )
                }}
              />
              {loading && <NotificationsLoading />}
            </>
          )}

          <div
            ref={needMoreNotificationsTriggerRef}
            style={{ height: 40, width: '100%', background: 'white' }}
          />
          <StickyButtonContainer>
            <StickyButton
              size="small"
              type="default"
              disabled={unReadNotificationsCount === 0}
              icon={<Icon component={MailOpen} style={{ fontSize: '1rem' }} />}
              onClick={() =>
                markAllNotificationsAsRead(
                  NotificationActionsCategory.Notifications
                )
              }
            >
              {t('notifications.markAllAsRead', {
                itemType: t('notifications.title'),
              })}
            </StickyButton>
          </StickyButtonContainer>
        </Tabs.TabPane>
        <Tabs.TabPane
          key={TabKeys.Comments}
          tab={
            <>
              {t('notifications.comments')}{' '}
              {loading ? (
                <Skeleton
                  active
                  title={{ width: 20, style: { marginBottom: 0, height: 16 } }}
                  style={{ height: 12, display: 'inline-flex', marginLeft: 4 }}
                  paragraph={false}
                />
              ) : (
                <UnreadCount count={unReadCommentNotificationsCount} />
              )}
            </>
          }
        >
          {!allCommentNotifications && loading ? (
            <NotificationsLoading />
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={allCommentNotifications}
              renderItem={notification => (
                <Notification
                  {...notification}
                  setReadHandler={readNotification}
                  loading={settingReadAtLoading}
                />
              )}
            />
          )}

          <div
            ref={needMoreCommentNotificationsTriggerRef}
            style={{ height: 40, width: '100%', background: 'white' }}
          />
          <StickyButtonContainer>
            <StickyButton
              type="default"
              size="small"
              disabled={unReadCommentNotificationsCount === 0}
              icon={<Icon component={MailOpen} style={{ fontSize: '1rem' }} />}
              onClick={() =>
                markAllNotificationsAsRead(NotificationActionsCategory.Comments)
              }
            >
              {t('notifications.markAllAsRead', {
                itemType: t('notifications.comments'),
              })}
            </StickyButton>
          </StickyButtonContainer>
        </Tabs.TabPane>
      </Tabs>
    </NotificationsListContainer>
  )
}

const NotificationsLoading: React.FC = () => {
  return (
    <div
      style={{
        height: '100%',
        minHeight: 100,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Spin />
    </div>
  )
}

const NotificationsListContainer = styled.div<{ isMobileOrTablet: boolean }>(
  ({ isMobileOrTablet }) => `
  max-height: ${isMobileOrTablet ? '100vh' : 'calc(80vh - 100px)'};
  min-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;

  .ant-tabs-nav {
    margin-bottom: 0;
    position: sticky;
    top: 0;
    z-index: 1000;
    background-color: #fff;
    padding: 0 1.25rem;
  }
  .ant-list {
    padding: 0 1.25rem;
  }
  .ant-tabs-tab {
    overflow: hidden;
  }
`
)

const StickyButtonContainer = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  background-color: #fff;
  box-shadow: 0 -1px 0 0 ${props => props.theme.grey5};
  border-radius: 0;
  z-index: 100;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StickyButton = styled(Button)`
  width: 100%;
  height: 100%;
  border-radius: 0;
  border: none;
`
